import React from 'react';
import {
  Box, Boxes, Copy, Flag, Heart, Package, Printer,
  ShoppingBag, Calendar, CreditCard, Signpost, Newspaper,
} from 'lucide-react';

/**
 * An order type's icon, by its code.
 *
 * Keyed on the code rather than a stored `menu_icon`, because the code is what
 * `/api/product-configs` always carries — the icon name lives on a different
 * collection, so every chip fell back to the same generic box.
 */
const CODE_ICONS = {
  OFF: Printer,       // offset press
  COR: Package,       // shipper / corrugated carton
  DPX: Box,           // folding carton
  FLX: Signpost,      // banners and signage
  WED: Heart,         // wedding stationery
  DIG: Copy,          // short-run digital prints
  BAG: ShoppingBag,   // carry bags
  CAL: Calendar,      // calendars
};

// A product type added by an admin has no code we know, so these read its name.
// Order matters — the first match wins, so the specific reading of a word comes
// before the general one ("Visiting Card" is a card, not a wedding).
const NAME_ICONS = [
  [/label|sticker|visiting|business card/i, CreditCard],
  [/wedding|invitation/i, Heart],
  [/carry|bag/i, ShoppingBag],
  [/calendar|diary|planner/i, Calendar],
  [/flex|signage|hoard|standee/i, Signpost],
  [/banner|flag/i, Flag],
  [/corrugat|shipper|carton/i, Package],
  [/box|pack/i, Boxes],
  [/book|magaz|brochure|leaflet|catalog/i, Newspaper],
  [/digital|photocop|print/i, Printer],
];

export function iconFor(config) {
  if (CODE_ICONS[config.code]) return CODE_ICONS[config.code];
  const name = `${config.name || ''} ${config.code || ''}`;
  const match = NAME_ICONS.find(([pattern]) => pattern.test(name));
  return match ? match[1] : Box;
}

/**
 * What is being made. One line, answered once.
 *
 * This is the first question on the job card because everything below it —
 * which processes exist, which fields the card has — is that answer's
 * consequence. It used to be asked at order entry, where nobody taking a phone
 * call could answer it.
 */
export default function ProductTypePicker({ configs, value, onSelect, locked }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="shrink-0 text-[11px] font-medium text-slate-600">
        Order Type<span className="text-brand-orange">*</span>
      </span>
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        {configs.map(config => {
          const Icon = iconFor(config);
          const selected = value === config.code;
          return (
            <button
              key={config.code}
              type="button"
              disabled={locked && !selected}
              onClick={() => onSelect(config.code)}
              title={locked && !selected ? 'Work has started — the type cannot change now' : config.name}
              data-testid={`order-type-${config.code}`}
              className={`flex items-center gap-1.5 rounded border px-2 py-1 text-xs whitespace-nowrap transition-colors ${
                selected
                  ? 'border-brand-blue bg-brand-blue font-semibold text-white'
                  : locked
                    ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-300'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-brand-blue/50 hover:bg-slate-50'
              }`}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              {config.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
